import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { getPlatformAuthToken, getPrincipalRoleFromToken } from '../utils/platformAuthToken';

interface PolicyMaxAiUsageStats {
  total: number;
  asset_checks: number;
  document_checks: number;
}

interface PolicyMaxAiUsageByBusiness extends PolicyMaxAiUsageStats {
  business_id: string;
  business_name: string;
}

interface PolicyMaxAiUsageCardProps {
  /** When set, show usage for this business (Integrations tab). Otherwise platform totals only. */
  businessId?: string;
  businessName?: string;
  /** Re-fetch when this changes (e.g. tab selected) */
  refreshKey?: number;
}

/** Policy Max AI usage — Super Admin only. */
const PolicyMaxAiUsageCard: React.FC<PolicyMaxAiUsageCardProps> = ({
  businessId,
  businessName,
  refreshKey = 0,
}) => {
  const [usage, setUsage] = useState<PolicyMaxAiUsageStats | null>(null);
  const [totals, setTotals] = useState<PolicyMaxAiUsageStats | null>(null);
  const [usageByBusiness, setUsageByBusiness] = useState<PolicyMaxAiUsageByBusiness[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSuperAdmin = getPrincipalRoleFromToken() === 'Super Admin';

  const fetchUsage = useCallback(async () => {
    if (!isSuperAdmin) return;
    const token = getPlatformAuthToken() || localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };

      if (businessId) {
        const singleRes = await axios.get(
          `${API_BASE_URL}/businesses/${businessId}/policy-max-ai-usage`,
          { headers }
        );
        if (singleRes.data?.success) {
          setUsage(singleRes.data.usage);
        }
      } else {
        setUsage(null);
      }

      const summaryRes = await axios.get(
        `${API_BASE_URL}/businesses/policy-max-ai-usage/summary`,
        { headers }
      );
      if (summaryRes.data?.success) {
        setUsageByBusiness(summaryRes.data.businesses || []);
        setTotals(summaryRes.data.totals || null);
      }
    } catch (err) {
      console.error('Error fetching Policy Max AI usage:', err);
      setError('Could not load usage stats.');
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, businessId]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage, refreshKey]);

  if (!isSuperAdmin) {
    return null;
  }

  const displayStats = businessId ? usage : totals;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
          <AutoAwesomeIcon color="secondary" sx={{ mt: 0.25 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              Policy Max AI — Key Documents & RAMS
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Powered by Policy Max
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Counts successful AI checks: asset compliance analysis and per-document reviews in the Files explorer and asset editor.
            </Typography>
          </Box>
          {loading && <CircularProgress size={22} />}
        </Box>

        {displayStats && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label={`Total: ${displayStats.total}`} color="secondary" variant="filled" sx={{ fontWeight: 700 }} />
            <Chip label={`Asset analyses: ${displayStats.asset_checks}`} variant="outlined" />
            <Chip label={`Document reviews: ${displayStats.document_checks}`} variant="outlined" />
          </Box>
        )}

        {businessId && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            This business: {businessName || businessId}
          </Typography>
        )}

        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Usage by business
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Business</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Asset</TableCell>
                <TableCell align="right">Document</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usageByBusiness.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {error
                        ? 'Usage stats could not be loaded'
                        : 'No AI checks recorded yet — run AI analyse or a document review in Files, then refresh this tab'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                usageByBusiness.map((row) => (
                  <TableRow
                    key={row.business_id}
                    selected={businessId ? row.business_id === businessId : false}
                    sx={businessId && row.business_id === businessId ? { bgcolor: 'action.selected' } : undefined}
                  >
                    <TableCell>{row.business_name}</TableCell>
                    <TableCell align="right"><strong>{row.total}</strong></TableCell>
                    <TableCell align="right">{row.asset_checks}</TableCell>
                    <TableCell align="right">{row.document_checks}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default PolicyMaxAiUsageCard;
